import { Component, Element, Event, EventEmitter, Method, Prop, Watch } from '@stencil/core';
import { GoogleMapsApi } from '~service/google-maps/google-maps-api';
import { debounce } from '~service/debounce';

declare const google: any;

function createId(): string {
  return `maps-marker-${Math.round(Math.random() * 1000000000)}`;
}

@Component({
  tag: 'uvex-google-map-marker',
  styleUrl: 'google-map-marker.scss',
})
export class GoogleMapMarker {
  @Prop()
  public latitude: number = 0;

  @Prop()
  public longitude: number = 0;

  @Prop()
  public showInfoWindow: boolean = false;

  @Prop()
  public icon?: string;

  @Prop()
  public locationId?: string;

  @Prop()
  public zIndex?: number;

  @Element() el: HTMLElement;

  @Event()
  mapsMouseOut: EventEmitter;

  @Event()
  mapsMouseOver: EventEmitter;

  @Event()
  mapsClick: EventEmitter;

  @Event()
  mapsInfoWindowOpen: EventEmitter;

  @Event()
  mapsInfoWindowClose: EventEmitter;

  private parent: HTMLUvexGoogleMapElement;

  private marker: google.maps.marker.AdvancedMarkerElement;

  private infoWindow: google.maps.InfoWindow;

  public id: string = createId();

  private updateMarker = debounce(async (): Promise<void> => {
    let options: google.maps.marker.AdvancedMarkerElementOptions = {
      position: {
        lat: this.latitude,
        lng: this.longitude,
      },
      zIndex: this.zIndex,
    };
    const contentContainer = (this.marker ? this.marker.content : document.createElement('div')) as HTMLElement;
    if (!contentContainer.classList.contains('marker-content-container')) {
      contentContainer.classList.add('marker-content-container');
    }

    if (this.icon) {
      const iconImg = contentContainer.querySelector('img') || document.createElement('img');
      iconImg.src = this.icon;

      if (!contentContainer.querySelector('img')) {
        contentContainer.appendChild(iconImg);
      }
    } else {
      contentContainer.querySelector('img')?.remove();
    }

    if (this.locationId) {
      contentContainer.dataset.locationId = this.locationId;
    }
    options.content = contentContainer;

    if (!this.marker) {
      this.marker = await this.parent.createMarker(options);
      this.marker.content.addEventListener('mouseover', event => this.mapsMouseOver.emit(event));
      this.marker.content.addEventListener('mouseout', event => this.mapsMouseOut.emit(event));
      this.marker.addListener('click', event => this.mapsClick.emit(event));
    } else {
      for (const key in options) {
        if (options.hasOwnProperty(key)) {
          // @ts-ignore
          this.marker[key] = options[key];
        }
      }
    }
    if (this.latitude && this.longitude) {
      await this.parent.addToBound(this.id, this.latitude, this.longitude);
    }
  });

  private options: google.maps.InfoWindowOptions = {
    pixelOffset: {
      width: -57,
      height: -25,
      equals: null,
    },
    minWidth: 300,
    maxWidth: 300,
  };

  async componentDidLoad() {
    await GoogleMapsApi.load();
    this.parent = this.el.closest('uvex-google-map') as HTMLUvexGoogleMapElement;

    await this.updateMarker();
    await this.updateInfoWindow();
  }

  async componentDidUpdate() {
    await this.updateInfoWindow();
  }

  async disconnectedCallback() {
    await this.closeInfoWindow();
    if (this.marker) {
      await this.parent.removeMarker(this.marker);
      this.removeMarker();
    }
    await this.parent.removeFromBound(this.id);
  }

  @Watch('latitude')
  @Watch('longitude')
  @Watch('icon')
  @Watch('zIndex')
  async handleLocationChange() {
    await this.updateMarker();
  }

  @Method()
  async removeMarker(): Promise<void> {
    if (this.marker) {
      this.marker.map = null;
      this.marker = null;
    }
  }

  private async updateInfoWindow() {
    if (this.showInfoWindow) {
      this.openInfoWindow();
    } else {
      this.closeInfoWindow();
    }
  }

  private async openInfoWindow() {
    if (!this.infoWindow) {
      this.infoWindow = new google.maps.InfoWindow();
      this.infoWindow.addListener('closeclick', _ => this.closeInfoWindow());
    }
    this.infoWindow.setOptions(this.options);
    this.infoWindow.setContent(this.el.innerHTML);
    await this.parent.openInfoWindow(this.infoWindow, this.marker);
    this.mapsInfoWindowOpen.emit(this.infoWindow);
  }

  private async closeInfoWindow() {
    if (this.infoWindow) {
      this.infoWindow.close();
      delete this.infoWindow;
      this.mapsInfoWindowClose.emit();
    }
  }
}
